<template>
  <div
    class="form-component w-full flex flex-col items-start gap-0.5"
    :class="{ 'with-error': state.isDirty && !state.isValid }">
    <label
      v-if="props.label.length > 0"
      :for="props.id"
      class="w-full !text-sm !text-gray text-start !font-normal"
      :class="{ '!text-red': state.isDirty && !state.isValid }">
      {{ props.label }}
    </label>

    <input
      type="email"
      :id="props.id"
      :aria-describedby="props.ariaDescribedby"
      :name="props.name"
      :placeholder="props.placeholder"
      autocomplete="email"
      class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full bg-transparent"
      :class="{ '!border-red': state.isDirty && !state.isValid }"
      v-model="state.emailField"
      @input="onInput"
      @change="onChange" />

    <div
      v-if="state.isDirty && !state.isValid"
      class="text-sm text-red">
      <span
        v-for="error in state.errors"
        :key="error"
        >{{ error }}</span
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, watch } from 'vue';

const props = defineProps({
  ariaDescribedby: String,
  label: { type: String, required: true },
  id: { type: String, required: true },
  name: { type: String, required: true },
  placeholder: { type: String, required: false },
  value: { type: String, required: true },
  isRequired: { type: Boolean, required: true },
  minLength: { type: Number, required: false },
  maxLength: { type: Number, required: false },
  triggerValidation: { type: Number, required: false },
  triggerResetValidation: { type: Number, required: false },
  serverError: { type: String, required: false },
});

const emit = defineEmits(['update:value', 'check-is-valid', 'on-change']);

const state = reactive({
  emailField: '',
  isDirty: false,
  isValid: false,
  errors: [] as string[],
});

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const validate = () => {
  state.isDirty = true;
  state.errors = [];

  if (props.serverError?.length) {
    state.errors.push(props.serverError);
  }

  if (props.isRequired && !state.emailField.trim()) {
    state.errors.push('This field is required. Please fill it in.');
  }

  if (state.emailField.length > 0 && !emailRegex.test(state.emailField)) {
    state.errors.push('Please enter a valid email address.');
  }

  if (props.minLength && state.emailField.length < props.minLength) {
    state.errors.push(`Minimum length is ${props.minLength} characters.`);
  }

  if (props.maxLength && state.emailField.length > props.maxLength) {
    state.errors.push(`Maximum length is ${props.maxLength} characters.`);
  }

  state.isValid = state.errors.length === 0;
  emit('check-is-valid', { name: props.name, isValid: state.isValid });
};

const reset = () => {
  state.isDirty = false;
  state.errors = [];
  state.isValid = false;
  emit('check-is-valid', { name: props.name, isValid: state.isValid });
};

const onInput = () => {
  if (state.emailField) {
    state.emailField = state.emailField.toLowerCase();
  }
  validate();
  emit('update:value', state.emailField);
  emit('on-change');
};

const onChange = () => {
  state.emailField = state.emailField.replace(/\s+/g, '').toLowerCase();
  emit('update:value', state.emailField);
  emit('on-change');
};

onMounted(() => {
  state.emailField = props.value || '';
});

watch(
  () => props.value,
  (newVal) => {
    if (newVal !== state.emailField) {
      state.emailField = newVal ?? '';
      reset();
    }
  }
);

watch(
  () => props.triggerValidation,
  () => {
    validate();
  }
);

watch(
  () => props.triggerResetValidation,
  () => {
    reset();
  }
);

watch(
  () => props.serverError,
  () => {
    validate();
  }
);
</script>
